/**
 * PortfoliYo Lab — Adaptive Allocation: configure a performance + risk adaptive policy for a sleeve
 * portfolio and compare it, walk-forward, against the portfolio's fixed and regime allocations.
 *
 * It works on the portfolios authored on the Strategies page; it is not a separate strategy system.
 * Pick a `regime-switch` portfolio (the Triple Blend's 80/10/10 ↔ 50/40/10, say) and its own
 * branches become arms A and B; the policy configured here is arm C. Pick an adaptive portfolio and
 * its saved policy is the starting point.
 *
 * The server runs everything: sleeve NAVs, the shared Engine allocator, the simulator. This page
 * holds a form and draws a result.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { labStrategyApi, type LabStrategy } from './labStrategyApi';
import { adaptiveApi, type AdaptiveResult, type Arm, type Decision } from './adaptive/adaptiveApi';
import {
  allocationFromForm,
  formFromDocument,
  formProblems,
  tripleBlendExample,
  type BoundRow,
  type Metric,
  type PolicyForm,
  type PortfolioDoc,
} from './adaptive/adaptivePolicy';

/** Categorical slots, fixed order (validated: CVD ΔE ≥ 9 adjacent; legends + table carry identity). */
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#7b61d1'];
const ARM_COLOR = { adaptive: SERIES[0], a: SERIES[1], b: SERIES[2] };

const fmtPct = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(digits)}%`;
const fmtNum = (v: number | null | undefined, digits = 2) =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(digits);
const iso = (d: Date) => d.toISOString().slice(0, 10);

const input = 'rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 focus:border-blue-500 focus:outline-none';
const numberInput = `${input} w-20 text-right tabular-nums`;
const card = 'rounded-lg border border-slate-200 bg-white p-4';
const h2 = 'mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600';

function NumberField({ label, value, onChange, step = 1, suffix, hint }: {
  label: string; value: number; onChange: (v: number) => void; step?: number; suffix?: string; hint?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm text-slate-700">
      <span>{label}</span>
      <span className="flex items-center gap-1">
        <input type="number" className={numberInput} value={value} step={step}
          onChange={(e) => onChange(Number(e.target.value))} />
        {suffix && <span className="text-slate-500">{suffix}</span>}
      </span>
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

function BoundsTable({ keys, names, table, onChange, showEnabled }: {
  keys: string[]; names: Record<string, string>; table: Record<string, BoundRow>;
  onChange: (key: string, row: BoundRow) => void; showEnabled?: boolean;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-slate-500">
          <th className="py-1 font-medium">Sleeve</th>
          {showEnabled && <th className="py-1 font-medium">Enabled</th>}
          <th className="py-1 text-right font-medium">Floor %</th>
          <th className="py-1 text-right font-medium">Cap %</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => {
          const row = table[key] ?? { minPct: 0, maxPct: 100, enabled: true };
          return (
            <tr key={key} className="border-t border-slate-100">
              <td className="py-1 pr-2 text-slate-800">{names[key] ?? key}</td>
              {showEnabled && (
                <td className="py-1">
                  <input type="checkbox" checked={row.enabled}
                    onChange={(e) => onChange(key, { ...row, enabled: e.target.checked })} />
                </td>
              )}
              <td className="py-1 text-right">
                <input type="number" className={numberInput} value={row.minPct} min={0} max={100}
                  onChange={(e) => onChange(key, { ...row, minPct: Number(e.target.value) })} />
              </td>
              <td className="py-1 text-right">
                <input type="number" className={numberInput} value={row.maxPct} min={0} max={100}
                  onChange={(e) => onChange(key, { ...row, maxPct: Number(e.target.value) })} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PolicyEditor({ form, setForm, keys, names }: {
  form: PolicyForm; setForm: (f: PolicyForm) => void; keys: string[]; names: Record<string, string>;
}) {
  const update = (patch: Partial<PolicyForm>) => setForm({ ...form, ...patch });
  const signalTotal = form.signals.reduce((s, r) => s + r.weightPct, 0);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className={card}>
        <h2 className={h2}>Performance score</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="py-1 font-medium">Metric</th>
              <th className="py-1 text-right font-medium">Lookback (months)</th>
              <th className="py-1 text-right font-medium">Weight %</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {form.signals.map((row, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1">
                  <select className={input} value={row.metric}
                    onChange={(e) => update({ signals: form.signals.map((r, j) => j === i ? { ...r, metric: e.target.value as Metric } : r) })}>
                    <option value="upi">UPI</option>
                    <option value="sortino">Sortino</option>
                    <option value="sharpe">Sharpe</option>
                    <option value="cagr">CAGR</option>
                  </select>
                </td>
                <td className="py-1 text-right">
                  <input type="number" className={numberInput} value={row.lookbackMonths} min={1} max={60}
                    onChange={(e) => update({ signals: form.signals.map((r, j) => j === i ? { ...r, lookbackMonths: Number(e.target.value) } : r) })} />
                </td>
                <td className="py-1 text-right">
                  <input type="number" className={numberInput} value={row.weightPct} min={0} max={100}
                    onChange={(e) => update({ signals: form.signals.map((r, j) => j === i ? { ...r, weightPct: Number(e.target.value) } : r) })} />
                </td>
                <td className="py-1 pl-2 text-right">
                  <button type="button" className="text-xs text-slate-500 hover:text-red-600"
                    onClick={() => update({ signals: form.signals.filter((_, j) => j !== i) })}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
          <button type="button" className="text-blue-700 hover:underline"
            onClick={() => update({ signals: [...form.signals, { metric: 'sharpe', lookbackMonths: 12, weightPct: 0 }] })}>
            + Add metric
          </button>
          <span className={Math.abs(signalTotal - 100) > 1e-6 ? 'font-medium text-red-700' : ''}>Total {signalTotal}%</span>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Each metric ranks the sleeves; the weighted ranks form a composite, and linear rank turns it into the
          performance allocation. Every sleeve stays in the portfolio.
        </p>
      </section>

      <section className={card}>
        <h2 className={h2}>Risk, blend and stability</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <NumberField label="Performance share" suffix="%" value={form.performancePct}
            onChange={(v) => update({ performancePct: v })} hint={`Risk (ERC) share ${100 - form.performancePct}%`} />
          <NumberField label="ERC lookback" suffix="months" value={form.riskLookbackMonths}
            onChange={(v) => update({ riskLookbackMonths: v })} />
          <label className="flex flex-col gap-1 text-sm text-slate-700">
            <span>Rebalance</span>
            <select className={input} value={form.frequency}
              onChange={(e) => update({ frequency: e.target.value as PolicyForm['frequency'] })}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          <NumberField label="Smoothing (keep previous)" suffix="%" value={form.smoothingPct}
            onChange={(v) => update({ smoothingPct: v })} />
          <NumberField label="Minimum change to trade" suffix="pp" value={form.minChangePct}
            onChange={(v) => update({ minChangePct: v })} />
          <NumberField label="Maximum step" suffix="pp" value={form.maxStepPct}
            onChange={(v) => update({ maxStepPct: v })} />
          <NumberField label="Rotation cost" suffix="bps" value={form.rotationCostBps} step={0.5}
            onChange={(v) => update({ rotationCostBps: v })} hint="On top-level sleeve turnover" />
        </div>
      </section>

      <section className={card}>
        <h2 className={h2}>Sleeve bounds</h2>
        <BoundsTable keys={keys} names={names} table={form.bounds} showEnabled
          onChange={(key, row) => update({ bounds: { ...form.bounds, [key]: row } })} />
      </section>

      <section className={card}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className={`${h2} mb-0`}>Regime bounds</h2>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.useRegime} onChange={(e) => update({ useRegime: e.target.checked })} />
            Use regime
          </label>
        </div>
        {form.useRegime ? (
          <>
            <p className="mb-2 text-xs text-slate-500">
              Regime at the previous close: <code className="font-mono">
                {String(form.regimeCondition['lhs-fn'])}({String(form.regimeCondition['lhs-val'])}) {String(form.regimeCondition.comparator)}{' '}
                {form.regimeCondition['rhs-fixed-value?'] ? String(form.regimeCondition['rhs-val'])
                  : `${String(form.regimeCondition['rhs-fn'])}(${String(form.regimeCondition['rhs-val'])}, ${JSON.stringify((form.regimeCondition['rhs-fn-params'] as any)?.window ?? '')})`}
              </code> → <strong>{form.regimeLabels.whenTrue}</strong>, else <strong>{form.regimeLabels.otherwise}</strong>.
            </p>
            {[form.regimeLabels.otherwise, form.regimeLabels.whenTrue].map((label) => (
              <div key={label} className="mb-3">
                <div className="mb-1 text-sm font-medium text-slate-800">{label}</div>
                <BoundsTable keys={keys} names={names} table={form.regimeBounds[label] ?? {}}
                  onChange={(key, row) => update({ regimeBounds: { ...form.regimeBounds, [label]: { ...(form.regimeBounds[label] ?? {}), [key]: row } } })} />
              </div>
            ))}
            {keys.length === 3 && (
              <button type="button" className="text-xs text-blue-700 hover:underline"
                onClick={() => setForm(tripleBlendExample(form, keys))}>
                Fill the Triple Blend example bounds (risk-on 60–85 / 5–30 / 5–15, risk-off 35–60 / 25–50 / 10–20)
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">The base sleeve bounds apply in every market.</p>
        )}
      </section>
    </div>
  );
}

function toSeries(arms: { key: string; nav: { date: string; value: number }[] }[]) {
  const byDate = new Map<string, Record<string, number | string>>();
  for (const arm of arms) {
    for (const point of arm.nav) {
      const row = byDate.get(point.date) ?? { date: point.date };
      row[arm.key] = point.value;
      byDate.set(point.date, row);
    }
  }
  return [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function SummaryTable({ arms }: { arms: { key: string; color: string; arm: Arm }[] }) {
  const cols: [string, (a: Arm) => string][] = [
    ['Total return', (a) => fmtPct(a.summary.total_return)],
    ['CAGR', (a) => fmtPct(a.summary.cagr)],
    ['Volatility', (a) => fmtPct(a.summary.annualized_volatility)],
    ['Sharpe', (a) => fmtNum(a.summary.sharpe)],
    ['Sortino', (a) => fmtNum(a.summary.sortino)],
    ['Max drawdown', (a) => fmtPct(a.summary.max_drawdown)],
    ['UPI', (a) => fmtNum(a.summary.upi)],
    ['Gross turnover', (a) => fmtPct(a.gross_turnover, 0)],
    ['Rotation cost', (a) => fmtPct(a.total_rotation_cost, 2)],
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500">
            <th className="py-1 pr-3 font-medium">Allocation</th>
            {cols.map(([c]) => <th key={c} className="py-1 pr-3 text-right font-medium">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {arms.map(({ key, color, arm }) => (
            <tr key={key} className="border-t border-slate-100">
              <td className="py-1.5 pr-3 text-slate-900">
                <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: color }} />
                {arm.name}
              </td>
              {cols.map(([c, f]) => <td key={c} className="py-1.5 pr-3 text-right tabular-nums text-slate-800">{f(arm)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DecisionAudit({ decision, names }: { decision: Decision; names: Record<string, string> }) {
  const stages: [string, (s: Decision['sleeves'][number]) => string][] = [
    ['Composite', (s) => fmtNum(s.composite_score, 3)],
    ['Rank', (s) => (s.performance_rank ?? '—').toString()],
    ['Perf. wt', (s) => fmtPct(s.performance_weight)],
    ['Vol.', (s) => fmtPct(s.annualized_volatility)],
    ['ERC wt', (s) => fmtPct(s.erc_weight)],
    ['Raw', (s) => fmtPct(s.raw_weight)],
    ['Bounds', (s) => `${fmtPct(s.min_weight, 0)}–${fmtPct(s.max_weight, 0)}`],
    ['Bounded', (s) => fmtPct(s.constrained_weight)],
    ['Previous', (s) => fmtPct(s.previous_weight)],
    ['Smoothed', (s) => fmtPct(s.smoothed_weight)],
    ['Step adj.', (s) => fmtPct(s.max_step_adjustment)],
    ['Final', (s) => fmtPct(s.final_weight)],
  ];
  return (
    <div className="overflow-x-auto">
      <p className="mb-2 text-xs text-slate-500">
        Signal {decision.signal_date} → executes {decision.execution_date} · regime {decision.regime ?? '—'} ·{' '}
        {decision.status}{decision.hold_reason ? ` (${decision.hold_reason})` : ''} · turnover {fmtPct(decision.turnover)} ·
        est. rotation cost {fmtPct(decision.estimated_rotation_cost, 3)}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-1 pr-2 font-medium">Sleeve</th>
            {stages.map(([s]) => <th key={s} className="py-1 pr-2 text-right font-medium">{s}</th>)}
          </tr>
        </thead>
        <tbody>
          {decision.sleeves.map((s) => (
            <tr key={s.sleeve_key} className="border-t border-slate-100">
              <td className="py-1 pr-2 text-slate-900">{names[s.sleeve_key] ?? s.sleeve_key}</td>
              {stages.map(([label, f]) => <td key={label} className="py-1 pr-2 text-right tabular-nums">{f(s)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {decision.sleeves[0]?.metrics.length > 0 && (
        <details className="mt-2 text-xs text-slate-600">
          <summary className="cursor-pointer">Metric values</summary>
          <table className="mt-1 w-full">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1 pr-2 font-medium">Sleeve</th>
                {decision.sleeves[0].metrics.map((m) => (
                  <th key={`${m.metric}${m.lookback_months}`} className="py-1 pr-2 text-right font-medium">
                    {m.metric.toUpperCase()} {m.lookback_months}m ({fmtPct(m.signal_weight, 0)})
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {decision.sleeves.map((s) => (
                <tr key={s.sleeve_key} className="border-t border-slate-100">
                  <td className="py-1 pr-2">{names[s.sleeve_key] ?? s.sleeve_key}</td>
                  {s.metrics.map((m) => (
                    <td key={`${m.metric}${m.lookback_months}`} className="py-1 pr-2 text-right tabular-nums">
                      {typeof m.value === 'number' ? m.value.toFixed(3) : m.value ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {decision.warnings.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs text-amber-800">
          {decision.warnings.map((w, i) => <li key={i}>{w.message}</li>)}
        </ul>
      )}
    </div>
  );
}

function Results({ result, names }: { result: AdaptiveResult; names: Record<string, string> }) {
  const arms = [
    { key: 'adaptive', color: ARM_COLOR.adaptive, arm: result.adaptive as Arm },
    ...result.benchmarks.map((arm, i) => ({ key: `bench${i}`, color: i === 0 ? ARM_COLOR.a : i === 1 ? ARM_COLOR.b : SERIES[3 + i - 2], arm })),
  ];
  const navData = useMemo(() => toSeries(arms.map((a) => ({ key: a.key, nav: a.arm.nav }))), [result]);
  const sleeveKeys = result.sleeves.map((s) => s.key);
  const allocationData = useMemo(() => {
    const byDate = new Map<string, Record<string, number | string>>();
    for (const row of result.allocation_history) {
      const entry = byDate.get(row.date) ?? { date: row.date };
      entry[row.sleeve] = row.target_weight * 100;
      byDate.set(row.date, entry);
    }
    return [...byDate.values()];
  }, [result]);
  const [auditIndex, setAuditIndex] = useState(result.rebalances.length - 1);
  const shown = result.rebalances[auditIndex] ?? result.latest_decision;

  return (
    <div className="space-y-4">
      {result.warnings.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {result.warnings.map((w, i) => <p key={i}>{w}</p>)}
        </div>
      )}
      <section className={card}>
        <h2 className={h2}>A / B / C comparison</h2>
        <p className="mb-3 text-xs text-slate-500">
          {result.first_execution_date} → {result.end_date} · every arm priced from the same sleeve NAVs with the same{' '}
          {result.adaptive.rotation_cost_bps} bps rotation cost on top-level turnover
          {result.sleeve_costs_bps > 0 ? ` (sleeves carry their own ${result.sleeve_costs_bps} bps inside their NAVs)` : ''}.
          {' '}{result.adaptive.rebalance_count} adaptive rebalances, {result.adaptive.held_count} sessions held.
          {result.failures_before_start > 0 && ` ${result.failures_before_start} earlier sessions could not fill the lookback and are not simulated.`}
        </p>
        <SummaryTable arms={arms} />
      </section>

      <section className={card}>
        <h2 className={h2}>Portfolio value (starting at 10,000)</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={navData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="#eceae4" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#52514e' }} minTickGap={48} />
              <YAxis tick={{ fontSize: 11, fill: '#52514e' }} width={64} domain={['auto', 'auto']}
                tickFormatter={(v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 })} />
              <Tooltip formatter={(v, key) => [Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 }), arms.find((a) => a.key === String(key))?.arm.name ?? String(key)]} />
              <Legend formatter={(key: string) => arms.find((a) => a.key === key)?.arm.name ?? key} wrapperStyle={{ fontSize: 12 }} />
              {arms.map((a) => (
                <Line key={a.key} dataKey={a.key} stroke={a.color} strokeWidth={2} dot={false} isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={card}>
        <h2 className={h2}>Adaptive sleeve targets</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={allocationData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="#eceae4" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#52514e' }} minTickGap={48} />
              <YAxis tick={{ fontSize: 11, fill: '#52514e' }} domain={[0, 100]} unit="%" width={48} />
              <Tooltip formatter={(v, key) => [`${Number(v ?? 0).toFixed(1)}%`, names[String(key)] ?? String(key)]} />
              <Legend formatter={(key: string) => names[key] ?? key} wrapperStyle={{ fontSize: 12 }} />
              {sleeveKeys.map((key, i) => (
                <Area key={key} dataKey={key} stackId="w" stroke="#ffffff" strokeWidth={1}
                  fill={SERIES[i % SERIES.length]} fillOpacity={0.9} isAnimationActive={false} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <details className="mt-2 text-xs text-slate-600">
          <summary className="cursor-pointer">Table view (per rebalance)</summary>
          <table className="mt-1 w-full">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1 pr-2 font-medium">Executes</th>
                {sleeveKeys.map((k) => <th key={k} className="py-1 pr-2 text-right font-medium">{names[k] ?? k}</th>)}
              </tr>
            </thead>
            <tbody>
              {result.rebalances.map((d) => (
                <tr key={d.execution_date} className="border-t border-slate-100">
                  <td className="py-1 pr-2">{d.execution_date}</td>
                  {sleeveKeys.map((k) => <td key={k} className="py-1 pr-2 text-right tabular-nums">{fmtPct(d.weights[k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </section>

      <section className={card}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className={`${h2} mb-0`}>Why the weights changed</h2>
          {result.rebalances.length > 0 && (
            <select className={input} value={auditIndex} onChange={(e) => setAuditIndex(Number(e.target.value))}>
              {result.rebalances.map((d, i) => (
                <option key={d.execution_date} value={i}>{d.execution_date} ({d.regime ?? 'no regime'})</option>
              ))}
            </select>
          )}
        </div>
        <DecisionAudit decision={shown} names={names} />
        <p className="mt-3 text-[11px] text-slate-400">
          {result.allocator_version} · {result.config_hash}
        </p>
      </section>

      {result.held_after_failure.length > 0 && (
        <section className={card}>
          <h2 className={h2}>Sessions that held the previous target after a failure</h2>
          <ul className="text-xs text-slate-700">
            {result.held_after_failure.map((d) => (
              <li key={d.execution_date}>{d.execution_date}: {d.errors.map((e) => e.message).join('; ')}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function AdaptiveAllocation() {
  const [portfolios, setPortfolios] = useState<LabStrategy[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [doc, setDoc] = useState<PortfolioDoc | null>(null);
  const [form, setForm] = useState<PolicyForm | null>(null);
  const [useSavedPolicy, setUseSavedPolicy] = useState(false);
  const [start, setStart] = useState(() => iso(new Date(Date.now() - 3 * 365 * 86400000)));
  const [end, setEnd] = useState(() => iso(new Date()));
  const [sleeveCostsBps, setSleeveCostsBps] = useState(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdaptiveResult | null>(null);
  const poll = useRef<number | null>(null);

  useEffect(() => {
    labStrategyApi.list()
      .then(({ symphonies }) => setPortfolios(symphonies.filter((s) => s.source_mode === 'portfolio')))
      .catch((e: Error) => setError(e.message));
    return () => { if (poll.current) window.clearTimeout(poll.current); };
  }, []);

  useEffect(() => {
    if (selectedId === null) return;
    setDoc(null); setForm(null); setResult(null); setError(null);
    labStrategyApi.definition(selectedId)
      .then((text) => {
        const parsed = JSON.parse(text) as PortfolioDoc;
        setDoc(parsed);
        setForm(formFromDocument(parsed));
        setUseSavedPolicy(parsed.allocation?.type === 'performance-risk-adaptive');
      })
      .catch((e: Error) => setError(`Could not read the portfolio: ${e.message}`));
  }, [selectedId]);

  const keys = useMemo(() => (doc?.sleeves ?? []).map((s) => s.key), [doc]);
  const names = useMemo(() => Object.fromEntries((doc?.sleeves ?? []).map((s) => [s.key, s.name ?? s.key])), [doc]);
  const problems = form && !useSavedPolicy ? formProblems(form) : [];
  const isAdaptiveDoc = doc?.allocation?.type === 'performance-risk-adaptive';

  const wait = useCallback((runId: number) => {
    adaptiveApi.result(runId).then((r) => {
      if (r.status === 'running') {
        poll.current = window.setTimeout(() => wait(runId), 3000);
        return;
      }
      setRunning(false);
      if (r.status === 'complete' && r.result) setResult(r.result);
      else setError(r.error ?? 'The run failed without a message.');
    }).catch((e: Error) => { setRunning(false); setError(e.message); });
  }, []);

  const run = async () => {
    if (selectedId === null || !form) return;
    setRunning(true); setError(null); setResult(null);
    try {
      const { run_id } = await adaptiveApi.submit({
        strategy_id: selectedId,
        start_date: start,
        end_date: end,
        allocation: useSavedPolicy ? null : allocationFromForm(form),
        sleeve_costs_bps: sleeveCostsBps,
      });
      wait(run_id);
    } catch (e) {
      setRunning(false);
      setError((e as Error).message);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Adaptive Allocation</h1>
        <p className="text-sm text-slate-600">
          Let a sleeve portfolio set its own sleeve weights from each sleeve&apos;s recent performance and risk, and
          compare that walk-forward against the portfolio&apos;s fixed and regime allocations.
        </p>
      </header>

      <section className={`${card} flex flex-wrap items-end gap-4`}>
        <label className="flex min-w-64 flex-col gap-1 text-sm text-slate-700">
          <span>Sleeve portfolio</span>
          <select className={input} value={selectedId ?? ''} onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Choose a portfolio…</option>
            {portfolios.map((p) => <option key={p.id} value={p.id}>{p.name ?? p.symphony_id}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-700">
          <span>Start</span>
          <input type="date" className={input} value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-700">
          <span>End</span>
          <input type="date" className={input} value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <NumberField label="Within-sleeve cost" suffix="bps" value={sleeveCostsBps} step={0.5}
          onChange={setSleeveCostsBps} />
        <button type="button" disabled={!form || running || problems.length > 0} onClick={run}
          className="rounded bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300">
          {running ? 'Running…' : 'Run comparison'}
        </button>
        {portfolios.length === 0 && !error && (
          <p className="text-sm text-slate-500">No sleeve portfolios yet — create one on the Strategies page.</p>
        )}
      </section>

      {doc && form && (
        <>
          <section className={card}>
            <h2 className={h2}>Allocation mode</h2>
            <div className="flex flex-wrap gap-6 text-sm text-slate-700">
              {isAdaptiveDoc && (
                <label className="flex items-center gap-2">
                  <input type="radio" checked={useSavedPolicy} onChange={() => setUseSavedPolicy(true)} />
                  The portfolio&apos;s saved adaptive policy
                </label>
              )}
              <label className="flex items-center gap-2">
                <input type="radio" checked={!useSavedPolicy} onChange={() => setUseSavedPolicy(false)} />
                Performance + risk adaptive, configured below
              </label>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {doc.allocation?.type === 'regime-switch'
                ? 'This portfolio’s own fixed (else-branch) and regime-switch allocations are the benchmarks.'
                : 'Benchmark: equal weight across the enabled sleeves.'}
              {' '}To make the policy the portfolio&apos;s own, set <code className="font-mono">allocation.type</code> to{' '}
              <code className="font-mono">performance-risk-adaptive</code> on the Strategies page.
            </p>
            {!useSavedPolicy && (
              <details className="mt-2 text-xs text-slate-600">
                <summary className="cursor-pointer">Allocation JSON for this policy</summary>
                <pre className="mt-1 max-h-64 overflow-auto rounded bg-slate-50 p-2 font-mono">{JSON.stringify(allocationFromForm(form), null, 2)}</pre>
              </details>
            )}
          </section>
          {!useSavedPolicy && <PolicyEditor form={form} setForm={setForm} keys={keys} names={names} />}
        </>
      )}

      {problems.length > 0 && (
        <ul className="list-disc rounded border border-red-200 bg-red-50 p-3 pl-8 text-sm text-red-800">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {running && (
        <p className="text-sm text-slate-600">
          Evaluating every sleeve over the range plus its lookback, then walking the allocator forward — this can take a few minutes for large sleeves.
        </p>
      )}
      {result && <Results result={result} names={names} />}
    </div>
  );
}
